import { useEffect, useRef, useState } from 'react';
import {
  defaultBarFor,
  defaultInventoryFor,
  defaultLoadableStepFor,
  plateStep,
  smallestLoadableStep,
  type LoadableContext,
  type PlateInventoryEntry,
  type PlateLoadMode,
} from '@heraion/shared';

// Per-device plate-calculator preferences, platform-neutral. ⛔ Nothing here
// may import AsyncStorage or touch `localStorage`: the ONE thing that differs
// between native and web-rnw is the two-method storage port below, so that is
// the only thing the twins (`plateConfig.ts` / `plateConfig.web.ts`) hold.
// Everything else — keys, parsing, fallbacks, the hook — lives here once and
// therefore cannot diverge.
//
// Single user per device today but we still namespace by version so the v2
// inventory shape coexists with whatever we change next.
//
// Two values, each stored ONCE PER UNIT (LG-37b E1): the bar weight and the
// plate inventory (the list of `{size, pairs}` the athlete has at home / at
// the gym). A pound gym racks 45/35/25/10/5/2.5 lb plates on a 45 lb bar —
// real iron, not the metric list converted — so the two units are two
// independent configurations, not one plus a multiplication
// (`equipment-model.md` «L'invariante che sopravvive a tutto»).
//
// ⚠️ The v1 keys below are the kg-only shape that shipped, with the field
// named `kg`. They are READ as a fallback for the kg unit and never written
// again: this module seeds from the new key and migrates nothing, so an
// athlete who saved a 0.5 kg plate keeps it.

/** The per-platform storage port. Two methods, both async — a web
 *  `localStorage` that throws (private mode, storage disabled) must reject
 *  into the same try/catch that already guards the native reads, never throw
 *  synchronously. */
export type PlateConfigStorage = {
  readItem: (key: string) => Promise<string | null>;
  writeItem: (key: string, value: string) => Promise<void>;
};

const BAR_KEY_V1 = 'jackedlogs.plateBar.v1';
const INV_KEY_V1 = 'jackedlogs.plateInventory.v1';

const barKeyFor = (unit: 'kg' | 'lb'): string =>
  `jackedlogs.plateBar.v2.${unit}`;
const invKeyFor = (unit: 'kg' | 'lb'): string =>
  `jackedlogs.plateInventory.v2.${unit}`;

// Sticky load-shape per exercise slug. Same shape as bilateralPreference.ts:
// per-slug key, per-device only, load/persist wrapped in try/catch so a
// storage hiccup never blocks the calculator.
const SHAPE_KEY_PREFIX = 'jackedlogs.plateLoadShape.v1.';

const shapeKeyFor = (slug: string): string => `${SHAPE_KEY_PREFIX}${slug}`;

// A slug that isn't a non-empty string can't key a per-exercise entry —
// treat it as "no preference" rather than coining a shared key.
const isUsableSlug = (slug: string): boolean =>
  typeof slug === 'string' && slug.length > 0;

/** Come il numero del set diventa ferro. 'standard' = coppie sui due lati;
 *  'single-side' = una pila sola su un lato (landmine, t-bar, slitte, leve per braccio). */
export type PlateLoadShape = 'standard' | 'single-side';

/** Bar weights offered per unit. ⛔ The lb row is not the kg row converted:
 *  45/35/25/15 are the bars a pound gym actually racks (Olympic, women's,
 *  training, technique/EZ). `0` stays last in both — it is the "no bar" case
 *  (belt, machine, dumbbell) and not a bar weight.
 *  Domain constant, not storage: it lives here so the two platform twins
 *  re-export ONE list instead of each keeping a copy that can drift.
 *
 *  ⭐ FIVE entries, not seven, since `LG-37b` E2 — the mockup's row is
 *  `20 · 15 · 10 · 7,5 · 0 · custom…`. Measured on the bench at 360pt: the old
 *  seven overflowed by 66px and `0` scrolled off screen in BOTH units, and `0`
 *  is the chip that opens the belt case (a weighted pull-up is bar 0 + one
 *  side). A row whose last chip nobody can see is a row with six options.
 *
 *  ⛔ NOTHING was taken away: 7 kg, 5 kg and 10 lb left the PRESETS, not the
 *  calculator. An athlete who had one of them saved still loads it — the
 *  reader below returns any stored finite value — and it shows up SELECTED in
 *  the custom chip with its own number. The custom field is also how a value
 *  that was never a preset gets named: a 7 kg EZ bar read on a pound row is
 *  15.4 lb, and until E2 that row had no selected chip at all. */
export const BAR_OPTIONS: Record<'kg' | 'lb', ReadonlyArray<number>> = {
  kg: [20, 15, 10, 7.5, 0],
  lb: [45, 35, 25, 15, 0],
};

// Parse a stored inventory blob into clean entries, or null when the blob
// isn't a usable custom inventory (missing / malformed / no valid rows). Kept
// pure + shared by loadPlateInventory and the rounding-step readers so the
// JSON cleaning lives once, and so the step reader can tell "customised"
// (real entries) from "never set" (null) — the two behave differently in LG-15.
//
// `field` is which key holds the denomination: `size` in the v2 blobs, `kg` in
// the v1 one this module still READS. Two names, one loop, because a second
// copy of this loop is how the legacy path stops matching the live one.
function parseStoredInventory(
  raw: string | null,
  field: 'size' | 'kg',
): PlateInventoryEntry[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const cleaned: PlateInventoryEntry[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const size = (item as Record<string, unknown>)[field];
      const pairs = (item as { pairs?: unknown }).pairs;
      if (typeof size !== 'number' || typeof pairs !== 'number') continue;
      if (size > 0 && pairs >= 0) cleaned.push({ size, pairs });
    }
    return cleaned.length > 0 ? cleaned : null;
  } catch {
    return null;
  }
}

/** Build the plate-config readers/writers over one storage port. Every
 *  function returned keeps the exact signature the twins used to declare
 *  themselves, so consumers import the same names from the same paths. */
export function makePlateConfig({ readItem, writeItem }: PlateConfigStorage) {
  async function loadPlateBar(unit: 'kg' | 'lb'): Promise<number> {
    try {
      const raw =
        (await readItem(barKeyFor(unit))) ??
        (unit === 'kg' ? await readItem(BAR_KEY_V1) : null);
      if (!raw) return defaultBarFor(unit);
      const n = parseFloat(raw);
      if (!Number.isFinite(n) || n < 0) return defaultBarFor(unit);
      return n;
    } catch {
      return defaultBarFor(unit);
    }
  }

  async function persistPlateBar(
    unit: 'kg' | 'lb',
    value: number,
  ): Promise<void> {
    try {
      await writeItem(barKeyFor(unit), String(value));
    } catch {
      // ignored
    }
  }

  // The athlete's stored inventory for `unit`, or null if they never saved one.
  //
  // ⚠️ The kg fallback to v1 is the thing that must not break: everyone who
  // configured plates before LG-37b E1 has their list under the OLD key, under
  // the OLD field name. Read it, map `kg → size`, and ⛔ never write it back —
  // "la costante semina, non migra". There is no lb fallback because v1 never
  // held pounds.
  async function storedInventory(
    unit: 'kg' | 'lb',
  ): Promise<PlateInventoryEntry[] | null> {
    const current = parseStoredInventory(
      await readItem(invKeyFor(unit)),
      'size',
    );
    if (current) return current;
    if (unit !== 'kg') return null;
    return parseStoredInventory(await readItem(INV_KEY_V1), 'kg');
  }

  async function loadPlateInventory(
    unit: 'kg' | 'lb',
  ): Promise<PlateInventoryEntry[]> {
    try {
      return (
        (await storedInventory(unit)) ??
        defaultInventoryFor(unit).map((e) => ({ ...e }))
      );
    } catch {
      return defaultInventoryFor(unit).map((e) => ({ ...e }));
    }
  }

  async function persistPlateInventory(
    unit: 'kg' | 'lb',
    inv: ReadonlyArray<PlateInventoryEntry>,
  ): Promise<void> {
    try {
      await writeItem(invKeyFor(unit), JSON.stringify(inv));
    } catch {
      // ignored
    }
  }

  // The finest loadable increment for the %1RM rounding path (LG-15), in the
  // unit's own iron. Decision (Antenore 2026-07-20, Option 1): floor to the
  // StrengthLog-style default, and refine from the athlete's plates ONLY if
  // they've configured them. The default inventory carries optimistic
  // micro-plates, so deriving from it un-configured would floor finer than the
  // conservative step the family expects — hence the key-absent branch returns
  // the constant, and only a real custom inventory derives a step.
  //
  // ⭐⭐ CN-39 — la FORMA di carico era `'pairs'` scritta qui dentro. Non era un
  // default motivato (il commento sopra giustifica il fallback COSTANTE, mai la
  // scelta del mode): era un'omissione. Il tasto «≈» il passo se lo costruiva
  // da sé col mode vero dell'esercizio, quindi sullo stesso ferro a lato
  // singolo un disco da 1,25 muoveva 1,25 di là e 2,5 di qua.
  //
  // ⛔ E il ramo SENZA inventario deve rispettare la forma quanto l'altro. La
  // prima stesura lo dimenticava e tornava la costante per entrambe: per chi
  // non ha configurato i dischi — quasi tutti — la cura non curava niente, e
  // in silenzio. Trovato dalla REVIEW, non dalla rilettura.
  //
  // La costante è un passo SIMMETRICO, cioè un disco per capo: il disco che
  // implica è la sua metà, e quello stesso disco su un capo solo muove la
  // barra della propria taglia. ⭐ Non è un'ipotesi: il disco più piccolo di
  // `DEFAULT_PLATE_INVENTORY` è 1,25 kg e quello lb è 2,5, e il commento di
  // `defaultLoadableStepFor` lo dichiara già («2×1.25 = 2.5, 2×2.5 = 5»).
  const defaultStepFor = (unit: 'kg' | 'lb', mode: PlateLoadMode): number =>
    plateStep(defaultLoadableStepFor(unit) / 2, mode);

  // ⭐ Legge l'inventario UNA volta per unità e ne ricava tutte e due le forme.
  // Una funzione chiamata quattro volte con un mode diverso leggeva lo storage
  // il doppio del necessario per rispondere alla stessa domanda.
  async function roundingStepsFor(
    unit: 'kg' | 'lb',
  ): Promise<{ pairs: number; single: number }> {
    const defaults = {
      pairs: defaultStepFor(unit, 'pairs'),
      single: defaultStepFor(unit, 'single'),
    };
    try {
      const custom = await storedInventory(unit);
      if (!custom) return defaults;
      return {
        pairs: smallestLoadableStep(custom, 'pairs', defaults.pairs),
        single: smallestLoadableStep(custom, 'single', defaults.single),
      };
    } catch {
      return defaults;
    }
  }

  // The athlete's finest loadable rung in BOTH units, as one `LoadableContext`
  // — the shape `roundLoadable`/`nextLoadableUp`/`nearestLoadable` already take.
  // One reader building the whole context, not two callers each fetching a
  // number and reassembling it: a context built in two places is a context
  // that can be built HALF (e.g. a kg-only ctx handed to a lb row), and
  // that half-built shape was exactly the bug (roundLoadable.ts's header).
  // ⭐ CN-39 — QUATTRO numeri, non due: due unità × due forme di carico. La
  // ragione è la stessa per cui ce n'erano già due invece di uno, e vale
  // identica per la forma: chi floora un seed su un landmine non deve andare a
  // ricavarsi il passo da solo, o la divergenza ricomincia da capo.
  async function loadRoundingContext(): Promise<LoadableContext> {
    const [kg, lb] = await Promise.all([
      roundingStepsFor('kg'),
      roundingStepsFor('lb'),
    ]);
    return {
      stepKg: kg.pairs,
      stepLb: lb.pairs,
      singleStepKg: kg.single,
      singleStepLb: lb.single,
    };
  }

  /** La forma scelta dall'atleta per QUESTO esercizio, o null se non l'ha mai scelta
   *  (⇒ il chiamante ricade sul seed del catalogo, `exercise.barLoading`). */
  async function loadPlateLoadShape(
    slug: string,
  ): Promise<PlateLoadShape | null> {
    if (!isUsableSlug(slug)) return null;
    try {
      const raw = await readItem(shapeKeyFor(slug));
      if (raw === 'standard') return 'standard';
      if (raw === 'single-side') return 'single-side';
      return null;
    } catch {
      return null;
    }
  }

  async function persistPlateLoadShape(
    slug: string,
    shape: PlateLoadShape,
  ): Promise<void> {
    if (!isUsableSlug(slug)) return;
    try {
      await writeItem(shapeKeyFor(slug), shape);
    } catch {
      // ignored
    }
  }

  // The athlete's finest loadable rung, held in a ref for the render paths that
  // need it synchronously when a gesture fires (plan-start hydrate, the swap's
  // weight carry). Each caller gets its OWN ref and its own one-shot load: the
  // point is to share the CODE, never the object — a shared ref instance would
  // couple two independent hooks through a value neither owns.
  //
  // `undefined` until the read lands, which is deliberate and safe: roundLoadable
  // then falls back to its documented default, i.e. the un-configured behaviour
  // rather than a wrong number. Ref and not state, so it cannot re-fire a
  // caller's gate-critical effects.
  //
  // React is NOT the platform axis here — `useEffect`/`useRef` behave the same
  // on native and on web-rnw — so the hook belongs in the core like the rest.
  function useRoundingContextRef(): { current: LoadableContext | undefined } {
    const ref = useRef<LoadableContext | undefined>(undefined);
    useEffect(() => {
      let cancelled = false;
      void (async () => {
        const ctx = await loadRoundingContext();
        if (!cancelled) ref.current = ctx;
      })();
      return () => {
        cancelled = true;
      };
    }, []);
    return ref;
  }

  // State twin of the ref above: render paths need a re-render when the read
  // lands, which a ref cannot give. Same one-shot load, same `undefined` until
  // it arrives.
  function useRoundingContext(): LoadableContext | undefined {
    const [ctx, setCtx] = useState<LoadableContext | undefined>(undefined);
    useEffect(() => {
      let cancelled = false;
      void (async () => {
        const loaded = await loadRoundingContext();
        if (!cancelled) setCtx(loaded);
      })();
      return () => {
        cancelled = true;
      };
    }, []);
    return ctx;
  }

  return {
    loadPlateBar,
    persistPlateBar,
    loadPlateInventory,
    persistPlateInventory,
    loadRoundingContext,
    loadPlateLoadShape,
    persistPlateLoadShape,
    useRoundingContextRef,
    useRoundingContext,
  };
}
